import {Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Collapse} from "@mui/material";
import {NavLink} from 'react-router-dom';
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {useAuth} from "./security/AuthProvider";
import {FC, SVGProps, useState} from "react";
import {
    IconMainNavigationAbsences, IconMainNavigationAutomations,
    IconMainNavigationDashboard, IconMainNavigationDocumentTemplates,
    IconMainNavigationEmployees,
    IconMainNavigationPayoffs,
    IconMainNavigationPayoffsPayoffs,
    IconMainNavigationPayoffsTransactions,
    IconMainNavigationPayrolls, IconMainNavigationPerformance,
    IconMainNavigationReporting,
    IconMainNavigationSales,
    IconMainNavigationSalesMySales,
    IconMainNavigationSalesOverview, IconMainNavigationSettings,
    IconMainNavigationTimetracking
} from "./components/Icons";
import {useTranslation} from "react-i18next";

interface NavigationProps {
    open: boolean;
}

interface NavSubmenuProps {
    icon: FC<SVGProps<SVGSVGElement>> | FC;
    label: string;
    roles: string[];
    children: React.ReactNode;
    defaultOpen?: boolean;
}

interface NavItemProps {
    to: string;
    disabled: boolean;
    icon: FC<SVGProps<SVGSVGElement>> | FC;
    label: string;
    roles?: string[];
}

const NavSubmenu: React.FC<NavSubmenuProps> = ({icon, label, roles, children, defaultOpen = false}) => {
    const {hasAuthority} = useAuth();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const Icon = icon;

    const handleToggle = () => {
        setIsOpen(prev => !prev);
    };

    // Check if user has any of the required roles
    const hasAccess = roles.length === 0 || roles.some(role => hasAuthority(role));

    if (!hasAccess) {
        return null;
    }

    return (
        <>
            <ListItemButton onClick={handleToggle}>
                <ListItemIcon sx={{minWidth: '40px', width: '24px'}}>
                    <Icon/>
                </ListItemIcon>
                <ListItemText primary={<Typography variant="navText">{label}</Typography>}/>
                {isOpen ? <ExpandLess/> : <ExpandMore/>}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        backgroundColor: (theme: any) => theme.palette.action.hover,
                        '&:hover': {
                            backgroundColor: (theme: any) => theme.palette.action.selected,
                        }
                    }}
                >
                    {children}
                </List>
            </Collapse>
        </>
    );
};

// Create a simpler NavItem component to reduce complexity
const NavItem = ({to, disabled, icon, label, roles = []}: NavItemProps) => {
    const {hasAuthority} = useAuth();
    const Icon = icon;

    // Check if user has any of the required roles
    const hasAccess = roles.length === 0 || roles.some(role => hasAuthority(role));

    if (!hasAccess) {
        return null;
    }

    return (
        <ListItemButton component={NavLink} to={to} disabled={disabled}>
            <ListItemIcon sx={{minWidth: '40px', width: '24px'}}>
                <Icon/>
            </ListItemIcon>
            <ListItemText primary={<Typography variant="navText">{label}</Typography>}/>
        </ListItemButton>
    );
};

const Navigation: React.FC<NavigationProps> = ({open}) => {

    const {t} = useTranslation();
    const {hasAuthority} = useAuth();
    const [openSalesSubmenu, setOpenSalesSubmenu] = useState(false);
    const [openTransactionsSubMenu, setOpenTransactionsSubMenu] = useState(false);

    const handleToggleSalesSubmenu = () => {
        setOpenSalesSubmenu((prev) => !prev);
    };

    const handleToggleTransactionsSubMenu = () => {
        setOpenTransactionsSubMenu((prev) => !prev);
    };

    const mainNavItems = [
        {
            to: '/dashboard',
            disabled: false,
            icon: IconMainNavigationDashboard,
            label: 'Dashboard',
            permissions: ['dashboard.view']
        },
        {
            to: '/absence-calendar',
            disabled: false,
            icon: IconMainNavigationAbsences,
            label: 'Abwesenheiten',
            permissions: ['absence-calendar.view']
        },
        {
            to: '/employees',
            disabled: false,
            icon: IconMainNavigationEmployees,
            label: 'Mitarbeiter',
            permissions: ['employee.view']
        },
        {
            to: '/timetracking',
            disabled: false,
            icon: IconMainNavigationTimetracking,
            label: 'Arbeitszeiterfassung',
            permissions: ['timesheet.view']
        },
        {
            to: '/analytics',
            disabled: false,
            icon: IconMainNavigationReporting,
            label: t('navigation.analytics'),
            permissions: ['report.view']
        },
        // {
        //     to: '/payrolls',
        //     disabled: false,
        //     icon: IconMainNavigationPayrolls,
        //     label: 'Lohnbuchhaltung',
        //     permissions: ['payroll.view']
        // },
        {
            to: '/document-templates',
            disabled: false,
            icon: IconMainNavigationDocumentTemplates,
            label: 'Dokumentenvorlagen',
            permissions: ['document-templates.view']
        },
        // {
        //     to: '/automations',
        //     disabled: false,
        //     icon: IconMainNavigationAutomations,
        //     label: 'Automatisierung',
        //     permissions: ['automation.view']
        // }
    ];

    const callIntellagentNavItems = [
        {
            to: '/custom/performance',
            disabled: false,
            icon: IconMainNavigationPerformance,
            label: 'Performance',
            roles: ['fourlook.performance.view']
        }
    ];

    const authorizedMainNavItems = mainNavItems.filter(item => {
        if (item.permissions.length === 0) return true;
        return item.permissions.some(role => hasAuthority(role));
    });

    const authorizedCallIntellagentNavItems = callIntellagentNavItems.filter(item => {
        if (item.roles.length === 0) return true;
        return item.roles.some(role => hasAuthority(role));
    });

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box sx={{flexGrow: 1}}>
                <List component="nav">
                    {/* Main navigation items */}
                    {authorizedMainNavItems.map((item) => (
                        <NavItem key={item.to} {...item} />
                    ))}

                    <div className={'mb-8'}/>

                    <NavSubmenu icon={IconMainNavigationPayoffs} label="Provisionen"
                                roles={['fourlook.commission.view']}>
                        <NavItem to="/custom/commissions/transactions" disabled={false}
                                 icon={IconMainNavigationPayoffsTransactions}
                                 label="Transaktionen" roles={['fourlook.commission.view']}/>

                        <NavItem to="/custom/commissions/payoffs" disabled={false}
                                 icon={IconMainNavigationPayoffsPayoffs}
                                 label="Abrechnungen" roles={['fourlook.commission.view']}/>
                    </NavSubmenu>

                    {/* Fourlook navigation items */}
                    {authorizedCallIntellagentNavItems.map((item) => (
                        <NavItem key={item.to} {...item} />
                    ))}

                    <NavSubmenu icon={IconMainNavigationSales} label="Verkaufszahlen" roles={['fourlook.sales.view']}>
                        <NavItem to="/custom/sales/mysales" disabled={false} icon={IconMainNavigationSalesMySales}
                                 label="Meine Verkäufe" roles={['fourlook.sales.view']}/>

                        <NavItem to="/custom/sales/overview" disabled={false} icon={IconMainNavigationSalesOverview}
                                 label="Übersicht" roles={['fourlook.sales.view']}/>
                    </NavSubmenu>
                </List>
            </Box>

            {/* Settings pushed to bottom */}
            <Box sx={{mt: 'auto'}}>
                <NavItem key={'/settings'} icon={IconMainNavigationSettings} label={'Einstellungen'} to={"/settings"}
                         disabled={false} roles={['settings.view']}/>
            </Box>
        </Box>
    );
};

export default Navigation;